import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
} from '@material-ui/core';
import { format, isSameDay } from 'date-fns';
import ServicesSection from './components/ServicesSection';
import DayPartSection from './components/DayPartSection';
import SupplyIntakeSection from './components/SupplyIntakeSection';
import { useStyles } from './styles';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import {
  getSingleRestaurantBaseWeekHours,
  set12HFormat,
  getSpecialEventDayByDay,
  getSpecialEventsReasons,
  clearRecentlyEditedBaseHours,
} from '../../redux/actions/newOperationHoursActions';
import {
  getRoutineDays,
  clearRecentlyEditedDeliveryDays,
} from '../../redux/actions/routineDaysActions';
import { getRestaurantServices } from '../../redux/actions/restaurantsActions';
import { useTranslation } from 'react-i18next';
import {
  getWeekDays,
  getWeekStartAndEnd,
  dayNames,
  findSpecialEventDataForDate,
  findSpecialEventReasonName,
  getTruncatedTextProps,
} from './common/utils';
import { getHolidays } from '../../redux/actions/holidays';

interface OperationHoursViewTabProps {
  selectedWeek?: Date;
}

function OperationHoursViewTab({
  selectedWeek: propSelectedWeek,
}: OperationHoursViewTabProps) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [selectedWeek, setSelectedWeek] = useState<Date>(
    propSelectedWeek || new Date(),
  );

  // Update local state when prop changes
  useEffect(() => {
    if (propSelectedWeek) {
      setSelectedWeek(propSelectedWeek);
    }
  }, [propSelectedWeek]);

  const weekDays = getWeekDays(selectedWeek);
  const today = new Date();

  const {
    RestaurantNumber: restaurantNumber,
    RestaurantId,
    brand,
    CountryId,
  } = useSelector((state: RootStateOrAny) => state.RestaurantsReducer.entity) ||
  {};
  useEffect(() => {
    if (restaurantNumber) {
      dispatch(
        getSingleRestaurantBaseWeekHours({
          restaurantNumber: restaurantNumber,
          brand: brand,
        }),
      );
      // Load delivery days for the restaurant
      dispatch(getRoutineDays(RestaurantId));
      // Load restaurant services
      if (RestaurantId) {
        dispatch(getRestaurantServices(RestaurantId));
      }
      // Load special event reasons
      dispatch(getSpecialEventsReasons());
    }
  }, [dispatch, restaurantNumber, brand, RestaurantId]);

  // Helper function to dispatch special event data
  const dispatchSpecialEventData = (weekDate: Date) => {
    if (RestaurantId) {
      const { startDate, endDate } = getWeekStartAndEnd(weekDate);
      dispatch(
        getSpecialEventDayByDay({
          restaurantId: RestaurantId,
          startDate,
          endDate,
        }),
      );
    }
  };

  // Dispatch special event data when selectedWeek changes
  useEffect(() => {
    if (RestaurantId) {
      dispatchSpecialEventData(selectedWeek);
    }
  }, [selectedWeek, RestaurantId]);

  useEffect(() => {
    if (CountryId) {
      dispatch(getHolidays(CountryId));
    }
  }, [CountryId]);

  const specialEventDayByDay = useSelector(
    (state: RootStateOrAny) =>
      state.NewOperationHoursReducer.specialEventDayByDay,
  );

  const specialEventReasons = useSelector(
    (state: RootStateOrAny) =>
      state.NewOperationHoursReducer.specialEventReasons,
  );

  const restaurantWeekHours = useSelector(
    (state: RootStateOrAny) =>
      state.NewOperationHoursReducer.restaurantWeekHours,
  );
  const baseWeekHours = restaurantWeekHours?.[restaurantNumber]?.hours || {};

  // Get delivery days for supply intake
  const { deliveryDays, recentlyEditedDeliveryDays } = useSelector(
    (state: RootStateOrAny) => state.RoutineDaysReducer,
  );

  // Get time format setting and recently edited base hours
  const { is12HFormat, recentlyEditedBaseHours } = useSelector(
    (state: RootStateOrAny) => state.NewOperationHoursReducer,
  );

  // Clear highlights when user clicks anywhere
  useEffect(() => {
    const hasServiceHighlights =
      recentlyEditedBaseHours &&
      Object.keys(recentlyEditedBaseHours).length > 0;
    const hasDeliveryDayHighlights =
      recentlyEditedDeliveryDays && recentlyEditedDeliveryDays.length > 0;

    if (hasServiceHighlights || hasDeliveryDayHighlights) {
      const clearHighlights = () => {
        // Clear service hours highlights
        dispatch(clearRecentlyEditedBaseHours());
        // Refresh the hours data
        if (restaurantNumber && brand) {
          dispatch(
            getSingleRestaurantBaseWeekHours({
              restaurantNumber: restaurantNumber,
              brand: brand,
            }),
          );
        }

        // Clear delivery days highlights
        dispatch(clearRecentlyEditedDeliveryDays());
        // Refresh delivery days data
        if (RestaurantId) {
          dispatch(getRoutineDays(RestaurantId));
        }
      };

      const handleClick = () => {
        // Clear highlights on any click, regardless of location
        clearHighlights();
      };

      // Use capture phase to catch clicks earlier, including navigation clicks
      document.addEventListener('click', handleClick, true);

      return () => {
        document.removeEventListener('click', handleClick, true);
      };
    }
  }, [
    recentlyEditedBaseHours,
    recentlyEditedDeliveryDays,
    dispatch,
    restaurantNumber,
    brand,
    RestaurantId,
  ]);

  // Clear highlights on component unmount (e.g., when navigating away)
  useEffect(() => {
    return () => {
      dispatch(clearRecentlyEditedBaseHours());
      dispatch(clearRecentlyEditedDeliveryDays());
    };
  }, [dispatch]);

  return (
    <div className={classes.root}>
      {/* Calendar Grid */}
      <div className={classes.calendarContainer}>
        {/* Day Headers */}
        <div className={classes.weekGrid}>
          {dayNames.map((dayName, index) => {
            const isToday = isSameDay(weekDays[index], today);

            // Check for special event data for this day
            const specialEventData = findSpecialEventDataForDate(
              specialEventDayByDay,
              weekDays[index],
            );

            // Get special event reason name if special event exists
            const specialEventReasonName = specialEventData?.specialEventReason
              ? findSpecialEventReasonName(
                  specialEventReasons,
                  specialEventData.specialEventReason,
                )
              : null;

            return (
              <div
                key={dayName}
                className={`${classes.dayHeader} ${
                  isToday ? classes.todayHeader : ''
                }`}
              >
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {t(
                    `app.common.calendar.${['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'][index]}_short`,
                    dayName,
                  )}{' '}
                  {format(weekDays[index], 'd')}
                </Typography>
                <div className={classes.labelsContainer}>
                  {specialEventReasonName &&
                    (() => {
                      const { displayText, tooltipTitle } =
                        getTruncatedTextProps(specialEventReasonName);
                      return (
                        <Typography
                          variant="caption"
                          className={classes.specialEventLabel}
                          title={tooltipTitle}
                        >
                          {displayText}
                        </Typography>
                      );
                    })()}
                </div>
              </div>
            );
          })}
        </div>

        <div className={classes.servicesSectionContainer}>
          <ServicesSection
            weekDays={weekDays}
            baseWeekHours={baseWeekHours}
            specialEventDayByDay={specialEventDayByDay}
            is12HFormat={is12HFormat}
            recentlyEditedBaseHours={recentlyEditedBaseHours}
          />
        </div>

        <DayPartSection
          weekDays={weekDays}
          baseWeekHours={baseWeekHours}
          is12HFormat={is12HFormat}
        />

        <SupplyIntakeSection
          deliveryDays={deliveryDays}
          recentlyEditedDeliveryDays={recentlyEditedDeliveryDays}
        />
      </div>

      {/* Time Format Checkbox */}
      <Box className={classes.timeFormatContainer}>
        <Typography className={classes.timeFormatLabel}>
          {t('operationHours.timeFormatCheckbox')}
        </Typography>
        <FormControl className={classes.timeFormatSelect}>
          <Select
            onChange={e =>
              dispatch(
                set12HFormat({
                  H12Format: JSON.parse(e.target.value as string),
                }),
              )
            }
            value={JSON.stringify(is12HFormat)}
            className={classes.timeFormatSelectInput}
            label={t('operationHours.timeFormatCheckbox')}
          >
            <MenuItem key="operationHours.12HourLabel" value={'true'}>
              {t('operationHours.12HourLabel')}
            </MenuItem>
            <MenuItem key="operationHours.24Hour" value={'false'}>
              {t('operationHours.24Hour')}
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
    </div>
  );
}

export default OperationHoursViewTab;
